import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { Clock } from 'lucide-react';
import { useAuth } from './useAuth';
import { suppressGuard } from '../guards/unsavedChanges';
import { useFocusTrap } from '../lib/useFocusTrap';
import { clearStoredWorkspaceId } from '../workspace/workspaceStorage';

/*
  Inactivity sign-out for the portal (sensitive financial data).

  Why a localStorage timestamp instead of a single setTimeout: backgrounded
  tabs are throttled and an installed PWA / sleeping device suspends JS, so a
  timer set for 15 min won't fire reliably. Instead we record `lastActivity`
  in localStorage and re-check elapsed time every second AND on every resume
  (visibilitychange / focus / pageshow). So when the tab/PWA wakes from the
  background it immediately notices the gap and signs out if the limit passed.

  localStorage is shared across tabs, so activity in any tab keeps them all
  alive; an idle sign-out broadcasts a signal so the other tabs drop too.

  This is a UX / defense-in-depth control, not a hard boundary — the real
  limit is the short Cognito token lifetime (see appcore platform auth.ts).
*/

export const IDLE_LIMIT_MS = 15 * 60 * 1000; // sign out after 15 min idle
const WARNING_LEAD_MS = 2 * 60 * 1000; // warn 2 min before
/**
 * The longest the sign-out waits for a page's own `beforeSignOut`. Ten seconds: long enough
 * for one save and its read-back on a slow connection, short enough that an API that never
 * answers cannot keep a signed-in page open much past the limit.
 */
export const BEFORE_SIGN_OUT_LIMIT_MS = 10_000;
const ACTIVITY_KEY = 'steward-app:lastActivity';
/**
 * The last time a person was seen doing something in any tab of this origin. Written from
 * these events only: mousemove, mousedown, keydown, touchstart, click and wheel, and
 * "Stay signed in". Not from a mount, and not from `scroll`, which a page (or the browser,
 * restoring a position on reload) can cause with nobody there. `lastActivity` above is
 * restarted by every mount of this guard, so it cannot say how long a browser has been left
 * alone; this can. Nothing in this component reads it: it exists for the snapshot
 * form's load-time rule (`intake/staleLoad.ts`).
 */
const USER_ACTIVITY_KEY = 'steward-app:lastUserActivity';
const LOGOUT_SIGNAL_KEY = 'steward-app:logoutSignal';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'] as const;

function readLastActivity(): number {
  try {
    const v = Number(localStorage.getItem(ACTIVITY_KEY));
    if (Number.isFinite(v) && v > 0) return v;
  } catch {
    // storage unavailable (e.g. private mode) — treat now as active so we
    // never falsely sign the user out.
  }
  return Date.now();
}

/**
 * The last time a person was seen doing something (see `USER_ACTIVITY_KEY`), or `null` when
 * there is none or storage cannot be read. Reads only; the snapshot form's load-time rule
 * decides what a missing or odd value means (`intake/staleLoad.ts`).
 */
export function readRecordedUserActivity(): number | null {
  try {
    const raw = localStorage.getItem(USER_ACTIVITY_KEY);
    if (raw === null) return null;
    const v = Number(raw);
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeUserActivity(t: number) {
  try {
    localStorage.setItem(USER_ACTIVITY_KEY, String(t));
  } catch {
    // ignore storage failures
  }
}

function writeLastActivity(t: number) {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(t));
  } catch {
    // ignore storage failures
  }
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export interface IdleTimeoutGuardProps {
  children: ReactNode;
  /**
   * Called once before the idle sign-out (the timeout itself, or "Sign out now"), for a page
   * that has to save or clear something of its own first: the snapshot form uses it
   * (`intake/IntakeFlow.tsx`). The sign-out waits for it to settle, for at most
   * {@link BEFORE_SIGN_OUT_LIMIT_MS}; after that the sign-out goes ahead and whatever the
   * hook had not yet done is not waited for (it is not cancelled either). A rejection or a
   * throw is swallowed. So the hook can delay the sign-out by up to that limit, and the only
   * thing that stops this component asking for it is the hook settling to exactly `false`
   * within the limit (below). It is called synchronously from the check that finds the limit
   * passed, so anything it does before its first `await` (covering the page, say) has
   * happened before the wait starts.
   *
   * What this component does NOT do: it does not hide or disable `children` while it waits.
   * A page that passes a hook covers itself. NOT run when another tab's idle sign-out
   * reaches this one through the storage signal: that path only drops the local session.
   * Without a hook nothing here is different from before the hook existed: the sign-out is
   * called at once (`IdleTimeout.test.tsx`).
   *
   * Opt-in: a hook that returns, or settles within the limit to, exactly `false` says the
   * page has already asked for its own sign-out, so this component does not call `signOut`.
   * By then it has latched `signingOut` for the life of the component and written the
   * cross-tab logout signal, so if the page's own sign-out then does not happen this
   * component does not try again. Any other value, including `undefined`, a rejection, a
   * throw and the limit passing first, leaves the behaviour above unchanged.
   */
  beforeSignOut?: () => Promise<void | false> | void | false;
}

export function IdleTimeoutGuard({ children, beforeSignOut }: IdleTimeoutGuardProps) {
  const { signOut } = useAuth();
  const oidc = useOidcAuth();
  const [warning, setWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(WARNING_LEAD_MS);
  const signingOut = useRef(false);
  const stayBtnRef = useRef<HTMLButtonElement>(null);
  const beforeSignOutRef = useRef(beforeSignOut);
  beforeSignOutRef.current = beforeSignOut;

  const doSignOut = useCallback(() => {
    if (signingOut.current) return;
    signingOut.current = true;
    try {
      // Tell other tabs to drop their session too.
      localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    const hook = beforeSignOutRef.current;
    if (!hook) {
      void signOut();
      return;
    }
    // Called now, in this turn, so its synchronous part is done before anything is awaited.
    let tidied: Promise<unknown>;
    try {
      tidied = Promise.resolve(hook());
    } catch {
      tidied = Promise.resolve();
    }
    void (async () => {
      let timer: number | undefined;
      let handled: unknown;
      try {
        handled = await Promise.race([
          tidied,
          new Promise<void>((resolve) => { timer = window.setTimeout(resolve, BEFORE_SIGN_OUT_LIMIT_MS); }),
        ]);
      } catch {
        // The page's own tidy-up failed. The sign-out still happens.
      } finally {
        window.clearTimeout(timer);
      }
      if (handled === false) return;
      await signOut();
    })();
  }, [signOut]);

  // Establish a fresh idle baseline for THIS page load. The persisted
  // `lastActivity` timestamp exists only to bridge a tab/PWA that is suspended
  // but still running (handled by the resume listeners below, which never
  // remount). A full page load — most importantly a returning user who just
  // signed in — must start its idle clock from now: otherwise a stale timestamp
  // left in localStorage by a previous session makes the first check() below
  // see hours of "idle" time and sign the user out the instant they land,
  // looking exactly like sign-in failing. The short Cognito token lifetime
  // (~30 min, see appcore platform auth.ts) remains the hard security boundary.
  useEffect(() => {
    writeLastActivity(Date.now());
    // Mount-only: must run before the first check() below and must NOT re-run on
    // later renders, or it would keep resetting the timer and defeat the timeout.
  }, []);

  // Re-check elapsed idle time every second and on every resume event.
  useEffect(() => {
    const check = () => {
      const elapsed = Date.now() - readLastActivity();
      if (elapsed >= IDLE_LIMIT_MS) {
        doSignOut();
      } else if (elapsed >= IDLE_LIMIT_MS - WARNING_LEAD_MS) {
        setWarning(true);
        setRemainingMs(IDLE_LIMIT_MS - elapsed);
      } else {
        setWarning(false);
      }
    };
    check();
    const id = window.setInterval(check, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
    };
  }, [doSignOut]);

  // Record activity (throttled). While the warning is up we ignore passive
  // activity — the user must explicitly choose, so the prompt doesn't flicker.
  useEffect(() => {
    let last = 0;
    const onActivity = (e: Event) => {
      if (warning) return;
      const t = Date.now();
      if (t - last < 1000) return;
      last = t;
      writeLastActivity(t);
      // Scroll keeps the idle clock alive as it always did, but is not evidence of a person.
      if (e.type !== 'scroll') writeUserActivity(t);
    };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true });
    return () => { for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity); };
  }, [warning]);

  // Cross-tab: a sign-out elsewhere drops this tab too.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LOGOUT_SIGNAL_KEY && e.newValue && !signingOut.current) {
        signingOut.current = true;
        // A sign-out in another tab drops this one too; suppress the guard so
        // ProtectedRoute's redirect to /login isn't caught by the nav blocker.
        suppressGuard();
        // steward-app#1401 review: this bypasses `useAuth().signOut()` (the tab that
        // actually drove the sign-out already ran that sequence, including token
        // revocation), so it must repeat the ONE side effect from it that matters to
        // THIS tab's own local state — clearing this tab's own remembered workspace.
        // Without it, a tab left open on a client (`AdvisorClients.tsx`'s tab-only
        // "Open") that receives another tab's sign-out keeps that pick in its own
        // sessionStorage, and the same advisor signing back in on THIS tab resurrects
        // it — the exact bug `clearStoredWorkspaceId` exists to prevent, reachable by a
        // second path. See that function's own doc for why only sessionStorage clears.
        clearStoredWorkspaceId();
        void oidc.removeUser(); // ProtectedRoute then redirects to /login
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [oidc]);

  // Traps Tab within the warning dialog (PS2) — declared before the "move
  // focus to the primary action" effect below so that effect's more specific
  // target wins (effects run in declaration order on mount).
  const trapRef = useFocusTrap<HTMLDivElement>(warning);

  // Move focus to the primary action when the warning appears.
  useEffect(() => {
    if (warning) stayBtnRef.current?.focus();
  }, [warning]);

  const staySignedIn = useCallback(() => {
    writeLastActivity(Date.now());
    writeUserActivity(Date.now());
    setWarning(false);
    // Refresh tokens so the session is actually extended, not just the timer.
    void oidc.signinSilent().catch(() => {
      // Refresh failure (e.g. refresh token expired) — the next protected
      // action will bounce to /login on its own.
    });
  }, [oidc]);

  return (
    <>
      {children}
      {warning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="idle-title"
          aria-describedby="idle-desc"
          ref={trapRef}
        >
          <div className="bg-surface border border-line shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] px-10 py-9 w-[380px] text-center rounded-token">
            <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-bronze/10">
              <Clock size={20} strokeWidth={1.75} aria-hidden className="text-bronze" />
            </div>
            <h2 id="idle-title" className="font-display text-[22px] font-semibold text-navy m-0 mb-2">
              Still there?
            </h2>
            <p id="idle-desc" className="text-[13px] text-mid m-0 mb-1 leading-relaxed font-sans">
              You&rsquo;ll be signed out for your security in
            </p>
            <p className="font-sans text-[28px] font-bold text-navy tabular-nums m-0 mb-7" aria-live="assertive">
              {formatCountdown(remainingMs)}
            </p>
            <div className="flex flex-col gap-2.5">
              <button
                ref={stayBtnRef}
                onClick={staySignedIn}
                className="w-full px-6 py-[12px] bg-navy text-cream border-none rounded-token text-[11px] font-bold font-sans uppercase tracking-[0.12em] cursor-pointer"
              >
                Stay signed in
              </button>
              <button
                onClick={doSignOut}
                className="w-full px-6 py-[11px] bg-transparent text-slate border-none text-[11px] font-semibold font-sans uppercase tracking-[0.1em] cursor-pointer"
              >
                Sign out now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
